import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import FormInput from '../../components/elements/form_input';
import PhoneInput from '../../components/elements/phone_input';
import Button from '../../components/elements/button';
import FileUpload from '../../components/elements/file_upload';
import EmailFirstAuth from '../../components/auth/EmailFirstAuth';
import { Lab, Calendar, Map, Mail, CircleCheck } from '../../assets/icons';
import '../../assets/css/lab/register.css';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { labApi } from '../../network/apis/lab';

const LabRegister = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    
    const [formData, setFormData] = useState({
        // Lab Information
        labName: '',
        labType: '',
        registrationNumber: '',
        establishedDate: '',
        
        // Contact Information
        labEmail: '',
        labPhone: '',
        address: '',
        city: '',
        state: '',
        
        // Staff Information
        director: {
            name: '',
            qualification: '',
            licenseNumber: ''
        },
        technicians: [],
        
        // Certifications & Equipment
        specializations: [],
        
        // Documents
        registrationCertificate: null,
        accreditationCertificate: null
    });

    const totalSteps = 5; // Account, Lab Info, Contact, Staff, Documents

    // Handle authentication completion from EmailFirstAuth component
    const handleAuthComplete = (userData) => {
        setIsAuthenticated(true);
        
        // Check if user is already a registered laboratory
        if (userData?.is_laboratory) {
            toast.success('You are already registered as a laboratory! Redirecting...');
            setTimeout(() => navigate('/lab'), 1500);
            return;
        }
        
        // Pre-fill email for lab contact if available
        if (userData?.email) {
            setFormData(prev => ({
                ...prev,
                labEmail: userData.email
            }));
        }
        
        // Move to next step
        setCurrentStep(2);
    };

    const handleFileUpload = (field, file) => {
        setFormData(prev => ({
            ...prev,
            [field]: file
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate required documents
        if (!formData.registrationCertificate) {
            toast.error('Please upload registration certificate');
            setCurrentStep(5); // Go to documents step
            return;
        }
        
        setIsSubmitting(true);
        try {
            toast.info('Submitting laboratory registration...');
            
            const labFormData = new FormData();
            labFormData.append('lab_name', formData.labName);
            labFormData.append('lab_type', formData.labType);
            labFormData.append('registration_number', formData.registrationNumber);
            labFormData.append('established_date', formData.establishedDate);
            labFormData.append('email', formData.labEmail);
            labFormData.append('phone', formData.labPhone);
            labFormData.append('address', formData.address);
            labFormData.append('city', formData.city);
            labFormData.append('state', formData.state);
            labFormData.append('director_name', formData.director.name);
            labFormData.append('director_qualification', formData.director.qualification);
            labFormData.append('director_license_number', formData.director.licenseNumber);
            
            // Append arrays as JSON strings
            if (formData.technicians.length > 0) {
                labFormData.append('technicians', JSON.stringify(formData.technicians));
            }
            if (formData.specializations.length > 0) {
                labFormData.append('specializations', JSON.stringify(formData.specializations));
            }
            
            // Append files
            if (formData.registrationCertificate) {
                labFormData.append('registration_certificate', formData.registrationCertificate);
            }
            if (formData.accreditationCertificate) {
                labFormData.append('accreditation_certificate', formData.accreditationCertificate);
            }
            
            await labApi.createLab(labFormData);
            toast.success('Registration submitted successfully! Your application is under review.');
            setTimeout(() => navigate('/lab'), 1500);
        } catch (error) {
            // Handle structured error responses with field-specific errors
            const errorData = error.response?.data;
            let errorMessage = 'Registration failed. Please try again.';
            
            if (errorData?.errors && typeof errorData.errors === 'object') {
                const fieldErrors = Object.entries(errorData.errors);
                if (fieldErrors.length > 0) {
                    const [, messages] = fieldErrors[0];
                    errorMessage = Array.isArray(messages) ? messages[0] : messages;
                }
            } else if (errorData?.message) {
                errorMessage = errorData.message;
            } else if (errorData?.error) {
                errorMessage = errorData.error;
            } else if (errorData?.detail) {
                errorMessage = errorData.detail;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleStepNavigation = (direction) => {
        // Prevent going back from step 2 if authenticated (can't change auth)
        if (direction === 'previous' && currentStep === 2 && isAuthenticated) {
            return;
        }
        
        if (direction === 'next') {
            // Step 2: Lab Information validation
            if (currentStep === 2) {
                if (!formData.labName.trim()) {
                    toast.error('Laboratory name is required');
                    return;
                }
                if (!formData.labType) {
                    toast.error('Please select laboratory type');
                    return;
                }
            }
            // Step 3: Contact validation
            if (currentStep === 3) {
                if (!formData.address.trim() || !formData.city.trim()) {
                    toast.error('Please fill in all address fields');
                    return;
                }
            }
            // Step 4: Staff validation
            if (currentStep === 4) {
                if (!formData.director.name.trim()) {
                    toast.error('Director name is required');
                    return;
                }
            }
            // Step 5: Documents - trigger submit
            if (currentStep === 5) {
                document.querySelector('.registration-form')?.requestSubmit();
                return;
            }
        }
        setCurrentStep(prev => direction === 'next' ? prev + 1 : prev - 1);
    };

    const stepLabels = ['Account', 'Lab Info', 'Contact', 'Staff', 'Documents'];
    const stepDescriptions = ['Sign in / Register', 'Basic Details', 'Location Info', 'Team Details', 'Certifications'];

    return (
        <div className="lab-register">
            <div className="register-container">
                <div className="register-header">
                    <div className="header-icon">
                        <img src={Lab} alt="Laboratory" />
                    </div>
                    <h1>Laboratory Registration</h1>
                    <p>Register your aquaculture testing laboratory</p>
                </div>

                <div className="progress-tracker">
                    {stepLabels.map((step, index) => (
                        <div 
                            key={index}
                            className={'progress-step ' + (currentStep > index + 1 ? 'completed ' : '') + (currentStep === index + 1 ? 'active' : '')}
                        >
                            <div className="step-indicator">
                                {currentStep > index + 1 ? (
                                    <img src={CircleCheck} className="step-check-icon" alt="completed" />
                                ) : (
                                    index + 1
                                )}
                            </div>
                            <span className="step-label">{step}</span>
                            <span className="step-description">{stepDescriptions[index]}</span>
                        </div>
                    ))}
                </div>

                <form action="#" onSubmit={handleSubmit} className="registration-form">
                    {/* Step 1: Account Authentication */}
                    {currentStep === 1 && (
                        <div className="form-step">
                            <div className="step-header">
                                <h2>Create Your Account</h2>
                                <p className="step-subtitle">Sign in or create an account to manage your laboratory</p>
                            </div>
                            
                            <EmailFirstAuth 
                                onAuthSuccess={handleAuthComplete}
                                userType="lab"
                            />
                        </div>
                    )}

                    {/* Step 2: Lab Information */}
                    {currentStep === 2 && (
                        <div className="form-step">
                            <div className="step-header">
                                <h2>Laboratory Information</h2>
                                <p className="step-subtitle">Tell us about your laboratory</p>
                            </div>
                            
                            <div className="form-section">
                                <div className="input-grid">
                                    <FormInput
                                        label="Laboratory Name"
                                        showLabel
                                        icon={<img src={Lab} alt="Lab" />}
                                        value={formData.labName}
                                        onChange={(e) => setFormData({...formData, labName: e.target.value})}
                                        required
                                        placeholder="e.g., Aqua Diagnostics Lab"
                                    />
                                    
                                    <FormInput
                                        label="Laboratory Type"
                                        showLabel
                                        type="select"
                                        placeholder="Select type..."
                                        value={formData.labType}
                                        onChange={(e) => setFormData({...formData, labType: e.target.value})}
                                        required
                                    >
                                        <option value="water_quality">Water Quality Testing</option>
                                        <option value="disease_diagnosis">Disease Diagnosis</option>
                                        <option value="feed_analysis">Feed Analysis</option>
                                        <option value="microbiology">Microbiology Lab</option>
                                        <option value="parasitology">Parasitology Lab</option>
                                        <option value="pathology">Pathology Lab</option>
                                        <option value="genetics">Genetics & DNA Testing</option>
                                        <option value="toxicology">Toxicology Lab</option>
                                        <option value="environmental">Environmental Testing</option>
                                        <option value="research">Research Laboratory</option>
                                        <option value="full_service">Full Service Laboratory</option>
                                    </FormInput>

                                    <FormInput
                                        label="Registration Number"
                                        showLabel
                                        value={formData.registrationNumber}
                                        onChange={(e) => setFormData({...formData, registrationNumber: e.target.value})}
                                        required
                                        placeholder="e.g., LAB-2024-XXXX"
                                    />

                                    <FormInput
                                        label="Established Date"
                                        showLabel
                                        icon={<img src={Calendar} alt="Calendar" />}
                                        type="date"
                                        value={formData.establishedDate}
                                        onChange={(e) => setFormData({...formData, establishedDate: e.target.value})}
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Contact Information */}
                    {currentStep === 3 && (
                        <div className="form-step">
                            <div className="step-header">
                                <h2>Contact Information</h2>
                                <p className="step-subtitle">Where is your laboratory located?</p>
                            </div>
                            
                            <div className="form-section">
                                <div className="input-grid">
                                    <FormInput
                                        label="Laboratory Email"
                                        showLabel
                                        icon={<img src={Mail} alt="Email" />}
                                        type="email"
                                        value={formData.labEmail}
                                        onChange={(e) => setFormData({...formData, labEmail: e.target.value})}
                                        required
                                        placeholder="lab@example.com"
                                    />
                                    
                                    <PhoneInput
                                        label="Laboratory Phone"
                                        showLabel
                                        value={formData.labPhone}
                                        onChange={(e) => setFormData({...formData, labPhone: e.target.value})}
                                        required
                                        defaultCountryCode="+234"
                                    />

                                    <FormInput
                                        label="Address"
                                        showLabel
                                        icon={<img src={Map} alt="Map" />}
                                        value={formData.address}
                                        onChange={(e) => setFormData({...formData, address: e.target.value})}
                                        required
                                        placeholder="Street address"
                                    />

                                    <FormInput
                                        label="City"
                                        showLabel
                                        value={formData.city}
                                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                                        required
                                        placeholder="City name"
                                    />

                                    <FormInput
                                        label="State"
                                        showLabel
                                        value={formData.state}
                                        onChange={(e) => setFormData({...formData, state: e.target.value})}
                                        required
                                        placeholder="State name"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 4: Staff Information */}
                    {currentStep === 4 && (
                        <div className="form-step">
                            <div className="step-header">
                                <h2>Staff Information</h2>
                                <p className="step-subtitle">Tell us about your team</p>
                            </div>
                            
                            <div className="form-section">
                                <div className="section-title">
                                    <h3>Laboratory Director</h3>
                                    <span className="section-badge">Required</span>
                                </div>
                                <div className="input-grid">
                                    <FormInput
                                        label="Director's Name"
                                        showLabel
                                        value={formData.director.name}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            director: {...formData.director, name: e.target.value}
                                        })}
                                        required
                                        placeholder="Full name"
                                    />
                                    
                                    <FormInput
                                        label="Qualification"
                                        showLabel
                                        value={formData.director.qualification}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            director: {...formData.director, qualification: e.target.value}
                                        })}
                                        required
                                        placeholder="e.g., PhD in Microbiology"
                                    />
                                    
                                    <FormInput
                                        label="License Number"
                                        showLabel
                                        value={formData.director.licenseNumber}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            director: {...formData.director, licenseNumber: e.target.value}
                                        })}
                                        required
                                        placeholder="Professional license number"
                                    />
                                </div>
                            </div>

                            <div className="form-section">
                                <div className="section-title">
                                    <h3>Laboratory Technicians</h3>
                                    <span className="section-badge optional">Optional</span>
                                </div>
                                
                                <div className="technicians-list">
                                    {formData.technicians.map((tech, index) => (
                                        <div key={index} className="technician-card">
                                            <FormInput
                                                label={'Technician ' + (index + 1)}
                                                value={tech.name}
                                                showLabel
                                                placeholder="Technician name"
                                                onChange={(e) => {
                                                    const techs = [...formData.technicians];
                                                    techs[index].name = e.target.value;
                                                    setFormData({...formData, technicians: techs});
                                                }}
                                            />
                                            <button 
                                                type="button"
                                                className="remove-technician-btn"
                                                onClick={() => {
                                                    const techs = formData.technicians.filter((_, i) => i !== index);
                                                    setFormData({...formData, technicians: techs});
                                                }}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                
                                <Button
                                    text="+ Add Technician"
                                    variant="outline"
                                    onClick={() => setFormData({
                                        ...formData,
                                        technicians: [...formData.technicians, { name: '' }]
                                    })}
                                />
                            </div>
                        </div>
                    )}

                    {/* Step 5: Documents & Certifications */}
                    {currentStep === 5 && (
                        <div className="form-step">
                            <div className="step-header">
                                <h2>Documents & Certifications</h2>
                                <p className="step-subtitle">Upload required documents and select specializations</p>
                            </div>
                            
                            <div className="form-section">
                                <div className="section-title">
                                    <h3>Required Documents</h3>
                                </div>
                                
                                <div className="document-uploads">
                                    <FileUpload
                                        label="Annual Practice License"
                                        field="registrationCertificate"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        maxSize={15 * 1024 * 1024}
                                        hint="Upload your annual practice license (PDF, JPG, PNG - max 15MB)"
                                        currentFile={formData.registrationCertificate}
                                        onUpload={handleFileUpload}
                                        required
                                    />
                                    
                                    <FileUpload
                                        label="Accreditation Certificate"
                                        field="accreditationCertificate"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        maxSize={5 * 1024 * 1024}
                                        hint="Optional: Lab accreditation certificate (PDF, JPG, PNG - max 5MB)"
                                        currentFile={formData.accreditationCertificate}
                                        onUpload={handleFileUpload}
                                    />
                                </div>
                            </div>

                            {/* <div className="form-section">
                                <div className="section-title">
                                    <h3>Laboratory Specializations</h3>
                                </div>
                                
                                <div className="specializations-grid">
                                    {[
                                        'Water Quality Analysis',
                                        'Disease Diagnosis',
                                        'Feed Quality Testing',
                                        'Microbiology',
                                        'Parasitology',
                                        'Chemical Analysis'
                                    ].map(spec => (
                                        <label key={spec} className="specialization-card">
                                            <input
                                                type="checkbox"
                                                checked={formData.specializations.includes(spec)}
                                                onChange={(e) => {
                                                    const specs = e.target.checked
                                                        ? [...formData.specializations, spec]
                                                        : formData.specializations.filter(s => s !== spec);
                                                    setFormData({...formData, specializations: specs});
                                                }}
                                            />
                                            <span className="specialization-checkmark"></span>
                                            <span className="specialization-label">{spec}</span>
                                        </label>
                                    ))}
                                </div>
                            </div> */}
                            
                            <div className="terms-section">
                                <label className="terms-checkbox">
                                    <input type="checkbox" required />
                                    <span className="terms-checkmark"></span>
                                    <span className="terms-text">
                                        I confirm that all information provided is accurate and true. 
                                        I agree to the <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>.
                                    </span>
                                </label>
                            </div>
                        </div>
                    )}

                    <div className="form-navigation">
                        {currentStep > 1 && !isAuthenticated && (
                            <Button
                                text="Previous"
                                variant="outline"
                                onClick={() => handleStepNavigation('previous')}
                                disabled={isSubmitting}
                            />
                        )}
                        {currentStep > 2 && isAuthenticated && (
                            <Button
                                text="Previous"
                                variant="outline"
                                onClick={() => handleStepNavigation('previous')}
                                disabled={isSubmitting}
                            />
                        )}
                        
                        {/* Step 1 has no button - EmailFirstAuth handles navigation */}
                        {currentStep > 1 && currentStep < totalSteps && (
                            <Button
                                text="Continue"
                                variant="primary"
                                onClick={() => handleStepNavigation('next')}
                            />
                        )}
                        
                        {currentStep === totalSteps && (
                            <Button
                                text={isSubmitting ? 'Submitting...' : 'Submit Registration'}
                                type="submit"
                                variant="primary"
                                loading={isSubmitting}
                                disabled={isSubmitting}
                            />
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LabRegister;
